import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FloppyDisk, ArrowCounterClockwise, Plus, Trash, CaretDown, CaretRight } from "@phosphor-icons/react";
import { useContent } from "@/contexts/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  navbar: "Navbar",
  auth: "Auth Pages (Login / Register)",
  pages: "Page Headers",
  common: "Common Buttons & Errors",
  features: "Features Section",
  about: "About Section",
  data: "Data Section",
  contact: "Contact Info",
  footer: "Footer",
  privacy_policy: "Privacy Policy",
  terms_of_service: "Terms of Service",
  api_docs: "API & Data Dictionary",
};

// Order in which sections are rendered
const SECTION_ORDER = [
  "navbar",
  "auth",
  "pages",
  "common",
  "features",
  "about",
  "data",
  "contact",
  "footer",
  "privacy_policy",
  "terms_of_service",
  "api_docs",
];

function Field({ label, value, onChange, multiline = false, rows = 3, testId }) {
  if (multiline) {
    return (
      <label className="block">
        <span className="text-[11px] text-stone-400 uppercase tracking-wider font-body block mb-1.5">{label}</span>
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white font-body focus:outline-none focus:border-emerald-500 resize-y"
          data-testid={testId}
        />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="text-[11px] text-stone-400 uppercase tracking-wider font-body block mb-1.5">{label}</span>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-white font-body focus:outline-none focus:border-emerald-500"
        data-testid={testId}
      />
    </label>
  );
}

function ItemList({ items, onChange, fields, testIdPrefix }) {
  const list = Array.isArray(items) ? items : [];
  const update = (idx, key, val) => {
    const next = [...list];
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const remove = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = () => {
    const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
    onChange([...list, blank]);
  };
  return (
    <div className="space-y-3">
      {list.map((item, idx) => (
        <div
          key={idx}
          className="bg-stone-800/40 border border-stone-700 rounded-lg p-3 space-y-2"
          data-testid={`${testIdPrefix}-item-${idx}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {fields.map((f) => (
              <Field
                key={f.key}
                label={f.label}
                value={item[f.key]}
                onChange={(v) => update(idx, f.key, v)}
                multiline={f.multiline}
                rows={f.rows || 2}
                testId={`${testIdPrefix}-${idx}-${f.key}`}
              />
            ))}
          </div>
          <button
            onClick={() => remove(idx)}
            className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
            data-testid={`${testIdPrefix}-remove-${idx}`}
          >
            <Trash size={12} /> Remove
          </button>
        </div>
      ))}
      <button
        onClick={add}
        className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600/10 border border-emerald-600/30"
        data-testid={`${testIdPrefix}-add`}
      >
        <Plus size={12} weight="bold" /> Add item
      </button>
    </div>
  );
}

function SectionWrapper({ title, sectionKey, dirty, onSave, onReset, saving, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-stone-900 rounded-xl border border-stone-800 overflow-hidden" data-testid={`cms-section-${sectionKey}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-stone-800/40 transition-colors"
        data-testid={`cms-toggle-${sectionKey}`}
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown size={14} className="text-stone-500" /> : <CaretRight size={14} className="text-stone-500" />}
          <span className="font-heading text-sm font-medium text-white">{title}</span>
          {dirty && <span className="text-[10px] uppercase tracking-wider text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">unsaved</span>}
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onReset}
            className="text-[11px] text-stone-400 hover:text-white px-2 py-1 rounded flex items-center gap-1"
            title="Reset to default"
            data-testid={`cms-reset-${sectionKey}`}
          >
            <ArrowCounterClockwise size={11} /> Reset
          </button>
          <button
            onClick={onSave}
            disabled={!dirty || saving}
            className="text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-700 disabled:text-stone-500 text-white px-3 py-1 rounded flex items-center gap-1 transition-colors"
            data-testid={`cms-save-${sectionKey}`}
          >
            <FloppyDisk size={11} weight="bold" /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </button>
      {open && <div className="p-5 border-t border-stone-800 space-y-4">{children}</div>}
    </div>
  );
}

export default function ContentEditor() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState({});
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState("");

  // Initialize draft from server content once
  useEffect(() => {
    if (content) {
      const snapshot = JSON.parse(JSON.stringify(content));
      setDraft(snapshot);
      setSavedSnapshot(snapshot);
    }
  }, [content]);

  const isDirty = useCallback(
    (key) => JSON.stringify(draft[key]) !== JSON.stringify(savedSnapshot[key]),
    [draft, savedSnapshot]
  );

  const updateSection = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const saveSection = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    setMessage("");
    try {
      await axios.put(
        `${API}/admin/content`,
        { sections: { [key]: draft[key] } },
        { withCredentials: true }
      );
      setSavedSnapshot((s) => ({ ...s, [key]: JSON.parse(JSON.stringify(draft[key])) }));
      setMessage(`Saved "${SECTION_LABELS[key]}" successfully`);
      refresh();
    } catch (e) {
      setMessage(`Failed to save: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  const resetSection = async (key) => {
    if (!window.confirm(`Reset "${SECTION_LABELS[key]}" to defaults? Unsaved changes will be lost.`)) return;
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await axios.post(
        `${API}/admin/content/reset`,
        { section: key },
        { withCredentials: true }
      );
      setMessage(`Reset "${SECTION_LABELS[key]}" to defaults`);
      await refresh();
    } catch (e) {
      setMessage(`Failed to reset: ${e.message}`);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  if (!content) {
    return <div className="text-stone-500 text-center py-12">Loading content...</div>;
  }

  return (
    <div className="space-y-4" data-testid="content-editor">
      <div>
        <h2 className="font-heading text-xl font-semibold mb-1">Site Content</h2>
        <p className="text-xs text-stone-500 font-body">
          Edit the public-facing text on Features, About, Data, Contact, Footer, Privacy Policy, Terms of Service, and API docs.
          Changes save per-section and are immediately visible on the live site.
        </p>
      </div>

      {message && (
        <div
          className="bg-emerald-600/10 border border-emerald-600/30 text-emerald-300 text-xs px-3 py-2 rounded-lg"
          data-testid="cms-message"
        >
          {message}
        </div>
      )}

      {SECTION_ORDER.map((key) => (
        <SectionWrapper
          key={key}
          title={SECTION_LABELS[key]}
          sectionKey={key}
          dirty={isDirty(key)}
          onSave={() => saveSection(key)}
          onReset={() => resetSection(key)}
          saving={!!saving[key]}
        >
          {key === "navbar" && <NavbarEditor value={draft.navbar} onChange={(v) => updateSection("navbar", v)} />}
          {key === "auth" && <AuthEditor value={draft.auth} onChange={(v) => updateSection("auth", v)} />}
          {key === "pages" && <PagesEditor value={draft.pages} onChange={(v) => updateSection("pages", v)} />}
          {key === "common" && <CommonEditor value={draft.common} onChange={(v) => updateSection("common", v)} />}
          {key === "features" && (
            <FeaturesEditor value={draft.features} onChange={(v) => updateSection("features", v)} />
          )}
          {key === "about" && <AboutEditor value={draft.about} onChange={(v) => updateSection("about", v)} />}
          {key === "data" && <DataEditor value={draft.data} onChange={(v) => updateSection("data", v)} />}
          {key === "contact" && <ContactEditor value={draft.contact} onChange={(v) => updateSection("contact", v)} />}
          {key === "footer" && <FooterEditor value={draft.footer} onChange={(v) => updateSection("footer", v)} />}
          {key === "privacy_policy" && (
            <Field
              label="Privacy Policy (Markdown — # heading, blank line for paragraph)"
              value={draft.privacy_policy}
              onChange={(v) => updateSection("privacy_policy", v)}
              multiline
              rows={18}
              testId="cms-privacy-text"
            />
          )}
          {key === "terms_of_service" && (
            <Field
              label="Terms of Service (Markdown — # heading, blank line for paragraph)"
              value={draft.terms_of_service}
              onChange={(v) => updateSection("terms_of_service", v)}
              multiline
              rows={18}
              testId="cms-terms-text"
            />
          )}
          {key === "api_docs" && <ApiDocsEditor value={draft.api_docs} onChange={(v) => updateSection("api_docs", v)} />}
        </SectionWrapper>
      ))}
    </div>
  );
}

// ─── Per-section editors ───

function FeaturesEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Section Label (eyebrow)" value={v.label} onChange={(x) => set("label", x)} testId="cms-features-label" />
        <Field label="Title (use Enter for line break)" value={v.title} onChange={(x) => set("title", x)} multiline rows={2} testId="cms-features-title" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Main Feature Cards</p>
        <ItemList
          items={v.items}
          onChange={(x) => set("items", x)}
          fields={[
            { key: "title", label: "Title" },
            { key: "icon", label: "Icon (CloudSun, Mountains, Wind, ChartLineUp, Globe, Bell)" },
            { key: "description", label: "Description", multiline: true, rows: 2 },
            { key: "href", label: "Link URL (e.g. /dashboard)" },
          ]}
          testIdPrefix="cms-features-item"
        />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Mini Feature Row</p>
        <ItemList
          items={v.mini_items}
          onChange={(x) => set("mini_items", x)}
          fields={[
            { key: "title", label: "Title" },
            { key: "icon", label: "Icon" },
            { key: "description", label: "Description", multiline: true, rows: 2 },
            { key: "href", label: "Link URL" },
          ]}
          testIdPrefix="cms-features-mini"
        />
      </div>
    </div>
  );
}

function AboutEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Section Label" value={v.label} onChange={(x) => set("label", x)} testId="cms-about-label" />
        <Field label="Title (use Enter for line break)" value={v.title} onChange={(x) => set("title", x)} multiline rows={2} testId="cms-about-title" />
      </div>
      <Field label="Paragraph 1" value={v.paragraph_1} onChange={(x) => set("paragraph_1", x)} multiline rows={3} testId="cms-about-p1" />
      <Field label="Paragraph 2" value={v.paragraph_2} onChange={(x) => set("paragraph_2", x)} multiline rows={3} testId="cms-about-p2" />
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Stats (Value + Label)</p>
        <ItemList
          items={v.stats}
          onChange={(x) => set("stats", x)}
          fields={[
            { key: "value", label: "Value (e.g. 195+)" },
            { key: "label", label: "Label (e.g. Countries Covered)" },
          ]}
          testIdPrefix="cms-about-stat"
        />
      </div>
    </div>
  );
}

function DataEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-3">
      <Field label="Section Label" value={v.label} onChange={(x) => set("label", x)} testId="cms-data-label" />
      <Field label="Title" value={v.title} onChange={(x) => set("title", x)} testId="cms-data-title" />
      <Field label="Subtitle" value={v.subtitle} onChange={(x) => set("subtitle", x)} multiline rows={2} testId="cms-data-subtitle" />
    </div>
  );
}

function ContactEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Email" value={v.email} onChange={(x) => set("email", x)} testId="cms-contact-email" />
      <Field label="Phone" value={v.phone} onChange={(x) => set("phone", x)} testId="cms-contact-phone" />
      <Field label="Address" value={v.address} onChange={(x) => set("address", x)} testId="cms-contact-address" />
      <Field label="Support Text" value={v.support_text} onChange={(x) => set("support_text", x)} multiline rows={2} testId="cms-contact-support" />
    </div>
  );
}

function FooterEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-4">
      <Field label="Tagline" value={v.tagline} onChange={(x) => set("tagline", x)} multiline rows={3} testId="cms-footer-tagline" />
      {[
        ["platform_links", "Platform Links"],
        ["resources_links", "Resources Links"],
        ["connect_links", "Connect Links"],
      ].map(([key, label]) => (
        <div key={key}>
          <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">{label}</p>
          <ItemList
            items={v[key]}
            onChange={(x) => set(key, x)}
            fields={[
              { key: "label", label: "Link label" },
              { key: "href", label: "Link href (e.g. /api-docs or /#features)" },
            ]}
            testIdPrefix={`cms-footer-${key}`}
          />
        </div>
      ))}
    </div>
  );
}

function ApiDocsEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-3">
      <Field label="Title" value={v.title} onChange={(x) => set("title", x)} testId="cms-apidocs-title" />
      <Field label="Description" value={v.description} onChange={(x) => set("description", x)} multiline rows={2} testId="cms-apidocs-desc" />
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">APIs / Data Sources</p>
        <ItemList
          items={v.apis}
          onChange={(x) => set("apis", x)}
          fields={[
            { key: "name", label: "Name" },
            { key: "category", label: "Category" },
            { key: "description", label: "Description", multiline: true, rows: 2 },
            { key: "base_url", label: "Base URL" },
            { key: "auth", label: "Auth (Public / API Key)" },
          ]}
          testIdPrefix="cms-apidocs-api"
        />
      </div>
    </div>
  );
}

function NavbarEditor({ value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Logo text" value={v.logo_text} onChange={(x) => set("logo_text", x)} testId="cms-navbar-logo" />
        <Field label="Sign-in label" value={v.signin_label} onChange={(x) => set("signin_label", x)} testId="cms-navbar-signin" />
        <Field label="CTA button label" value={v.cta_label} onChange={(x) => set("cta_label", x)} testId="cms-navbar-cta" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Nav Links</p>
        <ItemList
          items={v.links}
          onChange={(x) => set("links", x)}
          fields={[
            { key: "label", label: "Label" },
            { key: "href", label: "Href (e.g. #features or /carbon-hub)" },
            { key: "internal", label: "internal (true for / paths, false for #anchors)" },
          ]}
          testIdPrefix="cms-navbar-link"
        />
      </div>
    </div>
  );
}

function AuthEditor({ value, onChange }) {
  const v = value || {};
  const setSub = (sub, k, val) => onChange({ ...v, [sub]: { ...(v[sub] || {}), [k]: val } });
  return (
    <div className="space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Login Page</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-stone-800/40 p-3 rounded-lg">
          <Field label="Title" value={v.login?.title} onChange={(x) => setSub("login", "title", x)} testId="cms-auth-login-title" />
          <Field label="Subtitle" value={v.login?.subtitle} onChange={(x) => setSub("login", "subtitle", x)} testId="cms-auth-login-subtitle" />
          <Field label="Email label" value={v.login?.email_label} onChange={(x) => setSub("login", "email_label", x)} testId="cms-auth-login-email-label" />
          <Field label="Email placeholder" value={v.login?.email_placeholder} onChange={(x) => setSub("login", "email_placeholder", x)} testId="cms-auth-login-email-ph" />
          <Field label="Password label" value={v.login?.password_label} onChange={(x) => setSub("login", "password_label", x)} testId="cms-auth-login-pass-label" />
          <Field label="Password placeholder" value={v.login?.password_placeholder} onChange={(x) => setSub("login", "password_placeholder", x)} testId="cms-auth-login-pass-ph" />
          <Field label="Submit button" value={v.login?.submit_label} onChange={(x) => setSub("login", "submit_label", x)} testId="cms-auth-login-submit" />
          <Field label="'No account?' text" value={v.login?.no_account_text} onChange={(x) => setSub("login", "no_account_text", x)} testId="cms-auth-login-noacct" />
          <Field label="'Create one' link" value={v.login?.create_one_link} onChange={(x) => setSub("login", "create_one_link", x)} testId="cms-auth-login-create" />
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Register Page</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-stone-800/40 p-3 rounded-lg">
          <Field label="Title" value={v.register?.title} onChange={(x) => setSub("register", "title", x)} testId="cms-auth-reg-title" />
          <Field label="Subtitle" value={v.register?.subtitle} onChange={(x) => setSub("register", "subtitle", x)} testId="cms-auth-reg-subtitle" />
          <Field label="Name label" value={v.register?.name_label} onChange={(x) => setSub("register", "name_label", x)} testId="cms-auth-reg-name-label" />
          <Field label="Name placeholder" value={v.register?.name_placeholder} onChange={(x) => setSub("register", "name_placeholder", x)} testId="cms-auth-reg-name-ph" />
          <Field label="Email label" value={v.register?.email_label} onChange={(x) => setSub("register", "email_label", x)} testId="cms-auth-reg-email-label" />
          <Field label="Email placeholder" value={v.register?.email_placeholder} onChange={(x) => setSub("register", "email_placeholder", x)} testId="cms-auth-reg-email-ph" />
          <Field label="Password label" value={v.register?.password_label} onChange={(x) => setSub("register", "password_label", x)} testId="cms-auth-reg-pass-label" />
          <Field label="Password placeholder" value={v.register?.password_placeholder} onChange={(x) => setSub("register", "password_placeholder", x)} testId="cms-auth-reg-pass-ph" />
          <Field label="Submit button" value={v.register?.submit_label} onChange={(x) => setSub("register", "submit_label", x)} testId="cms-auth-reg-submit" />
          <Field label="'Already have account?' text" value={v.register?.has_account_text} onChange={(x) => setSub("register", "has_account_text", x)} testId="cms-auth-reg-hasacct" />
          <Field label="'Sign in' link" value={v.register?.signin_link} onChange={(x) => setSub("register", "signin_link", x)} testId="cms-auth-reg-signin" />
        </div>
      </div>
    </div>
  );
}

function PagesEditor({ value, onChange }) {
  const v = value || {};
  const setSub = (sub, k, val) => onChange({ ...v, [sub]: { ...(v[sub] || {}), [k]: val } });
  const pages = [
    ["dashboard", "Dashboard"],
    ["trees", "Tree Monitor"],
    ["carbon", "Carbon Estimator"],
    ["carbon_hub", "Carbon Hub"],
    ["marketplace", "Marketplace"],
    ["social", "Social"],
    ["data_center", "Data Center"],
    ["admin", "Admin Panel"],
    ["registry", "Carbon Registry"],
  ];
  return (
    <div className="space-y-3">
      {pages.map(([key, label]) => (
        <div key={key} className="bg-stone-800/40 p-3 rounded-lg">
          <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">{label}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Title" value={v[key]?.title} onChange={(x) => setSub(key, "title", x)} testId={`cms-pages-${key}-title`} />
            <Field label="Subtitle" value={v[key]?.subtitle} onChange={(x) => setSub(key, "subtitle", x)} testId={`cms-pages-${key}-subtitle`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function CommonEditor({ value, onChange }) {
  const v = value || {};
  const setSub = (sub, k, val) => onChange({ ...v, [sub]: { ...(v[sub] || {}), [k]: val } });
  const buttons = v.buttons || {};
  const errors = v.errors || {};
  const buttonKeys = ["save", "cancel", "submit", "delete", "approve", "reject", "back", "next", "loading", "saving"];
  const errorKeys = ["generic", "auth_required", "network", "upload_failed"];
  return (
    <div className="space-y-4">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Buttons</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 bg-stone-800/40 p-3 rounded-lg">
          {buttonKeys.map((k) => (
            <Field key={k} label={k} value={buttons[k]} onChange={(x) => setSub("buttons", k, x)} testId={`cms-common-btn-${k}`} />
          ))}
        </div>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 mb-2 font-body">Error Messages</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-stone-800/40 p-3 rounded-lg">
          {errorKeys.map((k) => (
            <Field key={k} label={k} value={errors[k]} onChange={(x) => setSub("errors", k, x)} multiline rows={2} testId={`cms-common-err-${k}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
